import { Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useState } from "react";
import Table from "../../components/UI/Table/Table";
import NoData from "../../components/UI/NoData";
import { IGetRefund, IRefundMediaFile } from "../../models/refunds";
import { formatValue } from "../../utils/conversions";
import RefundAttachmentsModal from "./RefundAttachmentsModal";

const translationPath = "page.refundsPage.";

interface IRefundsTableProps {
  refunds: IGetRefund[];
}

const RefundsTable = ({ refunds }: IRefundsTableProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [selectedMediaFiles, setSelectedMediaFiles] = useState<
    IRefundMediaFile[] | null
  >(null);

  const columns: GridColDef<IGetRefund>[] = [
    {
      field: "individual",
      headerName: `${t(translationPath + "beneficiary")}`,
      flex: 1,
      renderCell: (params) => params.row.individual.name,
    },
    {
      field: "serviceReason",
      headerName: `${t(translationPath + "service_reason")}`,
      flex: 1,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.refundData?.serviceReason || "-"}>
            <Typography
              variant="subtitle2"
              sx={{
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
            >
              {params.row.refundData?.serviceReason || "-"}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "createdDate",
      headerName: `${t(translationPath + "request_date")}`,
      minWidth: 150,
      maxWidth: 150,
      flex: 1,
      renderCell: (params) => {
        if (!params.row.refundData?.serviceDate) return "-";

        const localizedDate = dayjs(params.row.refundData.serviceDate)
          .locale(language)
          .format("L LTS");

        return <Typography variant="subtitle2">{localizedDate}</Typography>;
      },
    },
    {
      field: "amount",
      headerName: `${t(translationPath + "amount")}`,
      minWidth: 120,
      renderCell: (params) => {
        return (
          <Typography variant="subtitle2">
            {formatValue(
              params.row.refundData ? params.row.refundData.serviceValue : 0
            )}
          </Typography>
        );
      },
    },
    {
      field: "mediaFiles",
      headerName: `${t(translationPath + "attachments")}`,
      minWidth: 100,
      maxWidth: 100,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const mediaFiles = params.row.refundData?.mediaFiles;

        if (!mediaFiles || mediaFiles.length === 0) return "-";

        return (
          <Tooltip title={t(translationPath + "attachments")}>
            <IconButton
              size="small"
              onClick={() => setSelectedMediaFiles(mediaFiles)}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Grid item container xs={12} sm={12} md={12} xl={12}>
      <Grid item xs={12} sm={12} md={12} mt={2}>
        {refunds.length !== 0 && (
          <Table
            columns={columns}
            rows={refunds.flatMap((item, index) => ({
              ...item,
              id: item.individual.id + index,
            }))}
            rowHeight={70}
          />
        )}
      </Grid>
      {refunds.length === 0 && (
        <Grid container mt={5}>
          <Grid item xs={12} sm={12}>
            <NoData />
          </Grid>
        </Grid>
      )}
      <RefundAttachmentsModal
        open={!!selectedMediaFiles}
        closeModal={() => setSelectedMediaFiles(null)}
        mediaFiles={selectedMediaFiles ?? []}
      />
    </Grid>
  );
};

export default RefundsTable;
